import type { RendererProps } from "../core/element-registry"
import { useRenderContext } from "../core/render-context"
import { useAtomDefaults } from "../contexts/atom-defaults-context"
import { resolveEffectsStyle } from "../runtime/effects-resolver"
import { ElementRenderer } from "./element-renderer"
import { cn } from "../lib/utils"
import { TEXT_SIZE, TEXT_VARIANT, FONT_WEIGHT, TRACKING, pxToTextSize, resolveColor } from "../lib/style-resolver"

export function TextRenderer({ element }: RendererProps) {
  const { resolveSymbol, dispatchEvent, itemContext, mode } = useRenderContext()
  // Theme-v2 atom defaults: surfaced on data-* attrs so the fallback chain
  // can be asserted externally. Note: text.variant is a semantic typography
  // namespace (heading-xl, body, caption) distinct from shadcn theme variants;
  // the existing TEXT_VARIANT className logic below is unchanged.
  const atomDefaults = useAtomDefaults("text")
  const resolvedVariant = (element as { variant?: string }).variant
    ?? atomDefaults.defaultVariant
    ?? "default"
  const resolvedSize = (element as { size?: string }).size
    ?? atomDefaults.defaultSize
    ?? "default"
  // Support both flat format (element.text) and options format (element.options.text)
  const opts = element.options as Record<string, unknown> | undefined
  const rawText = element.text ?? opts?.text ?? ""
  let finalRaw = String(rawText)
  // Pre-resolve @{...} local references before passing to resolveSymbol
  if (finalRaw.includes("@{")) {
    finalRaw = finalRaw.replace(/@\{([^}]+)\}/g, (_, inner) => {
      const path = (inner as string).trim()
      if (path.startsWith("!")) {
        const val = resolveSymbol(`#{${path.slice(1)}}`)
        return String(!val)
      }
      const val = resolveSymbol(`#{${path}}`)
      return val != null ? String(val) : ""
    })
  }
  const resolved = resolveSymbol(finalRaw)
  const text = resolved == null ? "" : typeof resolved === "object" ? JSON.stringify(resolved, null, 2) : String(resolved)
  // Check if we have children to render (rehype pipeline creates nested text nodes)
  const hasChildren = element.children && element.children.length > 0

  // Resolve typography from options.typography object or flat properties
  const typography = (element as unknown as Record<string, unknown>).typography as Record<string, unknown> | undefined

  // Resolve numeric sizes from options (e.g., 40 → "3xl", 18 → "lg")
  // After normalizeElement(), options.size (number) becomes element.size (string like "64")
  const rawSize = element.size ?? opts?.size ?? typography?.fontSize
  const numericSize = typeof rawSize === "number" ? rawSize
    : typeof rawSize === "string" && /^\d+$/.test(rawSize) ? parseInt(rawSize, 10)
    : null
  const sizeToken = numericSize != null ? pxToTextSize(numericSize) : rawSize
  const rawWeight = element.weight ?? opts?.weight ?? typography?.fontWeight
  const rawColor = element.color ?? opts?.color ?? typography?.color
  // Resolve color bindings (e.g., "#{theme.colors.foreground}")
  const resolvedColor = typeof rawColor === "string" && rawColor.includes("#{")
    ? String(resolveSymbol(rawColor) ?? "")
    : rawColor

  const colorStr = resolvedColor ? String(resolvedColor) : ""
  const { className: colorClass, style: colorStyle } = resolveColor(colorStr || undefined)

  // Detect if className already has Tailwind classes that handle specific properties.
  // Bridge (rehype) pipeline generates BOTH className (original Tailwind) AND structured options
  // (lineHeight, color, etc.). Applying both causes double-application conflicts — e.g.,
  // lineHeight: 28 (px value from text-xl) rendered as unitless CSS → 28 × font-size = 560px!
  const cls = element.className ?? ""
  const clsHandlesLineHeight = /\b(leading-|text-(xs|sm|base|lg|xl|2xl|3xl|4xl|5xl|6xl|7xl|8xl|9xl)\b)/.test(cls)
  const clsHandlesColor = /\btext-(?!xs\b|sm\b|base\b|lg\b|xl\b|2xl\b|3xl\b|4xl\b|5xl\b|6xl\b|7xl\b|8xl\b|9xl\b|center\b|left\b|right\b|justify\b|wrap\b|balance\b|pretty\b|ellipsis\b|clip\b|nowrap\b)\S/.test(cls)
  const clsHandlesAlign = /\btext-(center|left|right|justify)\b/.test(cls)
  const clsHandlesTracking = /\btracking-/.test(cls)

  const classes = cn(
    element.variant && (TEXT_VARIANT[element.variant] ?? ""),
    sizeToken ? (TEXT_SIZE[String(sizeToken)] ?? "") : undefined,
    rawWeight ? (FONT_WEIGHT[String(rawWeight)] ?? "") : undefined,
    // Skip colorClass when className already has Tailwind color utility (avoids duplication)
    !clsHandlesColor ? colorClass : undefined,
    element.tracking && (TRACKING[element.tracking] ?? ""),
    element.lineClamp && `line-clamp-${element.lineClamp}`,
    element.margin,
    element.className,
  )

  // Only apply inline color when className doesn't already handle it via Tailwind color utilities
  const inlineStyle: React.CSSProperties = clsHandlesColor ? {} : { ...colorStyle }
  const letterSpacing = opts?.letterSpacing
  if (typeof letterSpacing === "number" && !clsHandlesTracking) {
    inlineStyle.letterSpacing = `${letterSpacing}px`
  }
  const align = opts?.align as string | undefined
  if (align && !clsHandlesAlign) {
    inlineStyle.textAlign = align as React.CSSProperties["textAlign"]
  }
  // lineHeight from TailwindClassParser is in pixels (e.g., 28 from text-xl's 1.75rem line-height).
  // Applied as unitless CSS, the browser interprets it as a multiplier (28 × font-size = 560px!).
  // When className already has leading-* or text-* with implicit line-height, skip entirely.
  // Otherwise, add px suffix for values that are clearly pixel values (>= 4).
  const lineHeight = opts?.lineHeight
  if (typeof lineHeight === "number" && !clsHandlesLineHeight) {
    inlineStyle.lineHeight = lineHeight >= 4 ? `${lineHeight}px` : lineHeight
  }
  // Apply opacity from styling
  const styling = element.styling as Record<string, unknown> | undefined
  if (styling?.opacity != null) {
    inlineStyle.opacity = Number(styling.opacity)
  }

  // Apply gradientText effect
  const gradientStyle = element.effects?.gradientText
    ? resolveEffectsStyle({ gradientText: element.effects.gradientText })
    : {}
  const finalStyle = { ...inlineStyle, ...gradientStyle }

  // Use semantic heading elements for heading variants (explicit tag override takes priority)
  const tagMap: Record<string, React.ElementType> = { h1: "h1", h2: "h2", h3: "h3", h4: "h4", h5: "h5", h6: "h6", p: "p", span: "span", div: "div", pre: "pre", code: "code", label: "label" }
  // className contains block-level CSS → must render as block element (div/p)
  // Detects: block, text-center, text-right, max-w-*, text-balance, w-full, flex, grid
  const hasBlockClassName = element.className != null && /(^|\s)(block|text-center|text-right|max-w-\S+|text-balance|w-full|flex|grid)(\s|$)/.test(element.className)
  const Tag = (element.tag && tagMap[element.tag])
    ? tagMap[element.tag]
    : (element.variant && tagMap[element.variant])  // "h1"~"h6", "p" variants → matching HTML tag
    ? tagMap[element.variant]
    : element.variant?.startsWith("heading-xl") ? "h1"
    : element.variant?.startsWith("heading-lg") ? "h1"
    : element.variant?.startsWith("heading-md") ? "h2"
    : element.variant?.startsWith("heading-sm") ? "h3"
    : element.variant === "caption" ? "p"
    : (numericSize != null && numericSize >= 48) ? "h1"
    : (numericSize != null && numericSize >= 30) ? "h2"
    : (numericSize != null && numericSize >= 24) ? "h3"
    : hasBlockClassName ? "div"
    : "span"

  const hasStyle = Object.keys(finalStyle).length > 0

  // Tap event support: text elements with event bindings become clickable
  const hasTap = !!element.events?.tap
  const handleClick = hasTap
    ? (e: React.MouseEvent) => {
        e.preventDefault()
        if (mode !== "edit") e.stopPropagation()
        dispatchEvent(element.id, "tap", itemContext)
      }
    : undefined
  const dataAttrs = {
    "data-testid": `text-${element.id}`,
    "data-atom-type": "text",
    "data-variant": resolvedVariant,
    "data-size": resolvedSize,
  }
  const clickProps = handleClick
    ? { onClick: handleClick, role: "button" as const, style: { ...hasStyle ? finalStyle : {}, cursor: "pointer" } }
    : { style: hasStyle ? finalStyle : undefined }

  // If no direct text but has children (from rehype pipeline), render children instead
  if (!text && hasChildren) {
    return (
      <Tag className={classes} {...clickProps} {...dataAttrs}>
        {element.children!.map((child, idx) => (
          <ElementRenderer key={child.id ?? `child-${idx}`} element={child} />
        ))}
      </Tag>
    )
  }

  return <Tag className={classes} {...clickProps} {...dataAttrs}>{text}</Tag>
}

// All token maps moved to lib/style-resolver.ts
